
import React from "react";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate
} from "react-router-dom";

import {
    AuthProvider,
    useAuth
} from "./context/AuthContext";

import "./mobile-responsive.css";

import Layout from "./components/Layout";

// ERP pages
import Dashboard from "./pages/Dashboard";
import Chambers from "./pages/Chambers";
import Customers from "./pages/Customers";
import Delivery from "./pages/Delivery";
import DeliveryChallans from "./pages/DeliveryChallans";
import GoodsReceived from "./pages/GoodsReceived";
import Inventory from "./pages/Inventory";
import Lots from "./pages/Lots";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Users from "./pages/Users";
import Vehicles from "./pages/Vehicles";
import CustomerCharges from "./pages/CustomerCharges";
import CashVouchers from "./pages/CashVouchers";




// Authentication
import Login from "./pages/Login";

// Standalone print pages (without sidebar)
import GRNPrint from "./pages/GRNPrint";
import DeliveryChallanView from "./pages/DeliveryChallanView";

/* =====================================================
   PROTECTED ROUTE
===================================================== */

const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f4f7fb",
                    color: "#26324b",
                    fontFamily: "Arial, sans-serif",
                    fontSize: "16px"
                }}
            >
                Loading ERP...
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

/* =====================================================
   PUBLIC LOGIN ROUTE
===================================================== */

const PublicLoginRoute = () => {
    const { isAuthenticated } = useAuth();

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return <Login />;
};

/* =====================================================
   APP
===================================================== */

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>

                    {/* LOGIN */}
                    <Route
                        path="/login"
                        element={<PublicLoginRoute />}
                    />

                    {/* GRN PRINT - NO SIDEBAR */}
                    <Route
                        path="/goods-received/print/:id"
                        element={
                            <ProtectedRoute>
                                <GRNPrint />
                            </ProtectedRoute>
                        }
                    />

                    {/* DELIVERY CHALLAN PRINT - NO SIDEBAR */}
                    <Route
                        path="/delivery-challans/print/:id"
                        element={
                            <ProtectedRoute>
                                <DeliveryChallanView />
                            </ProtectedRoute>
                        }
                    />

                    {/* MAIN ERP LAYOUT - SIDEBAR VISIBLE */}
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute>
                                <Layout />
                            </ProtectedRoute>
                        }
                    >
                        {/* DEFAULT PAGE */}
                        <Route
                            index
                            element={
                                <Navigate
                                    to="/dashboard"
                                    replace
                                />
                            }
                        />

                        {/* DASHBOARD */}
                        <Route
                            path="dashboard"
                            element={<Dashboard />}
                        />

                        {/* GOODS RECEIVED */}
                        <Route
                            path="goods-received"
                            element={<GoodsReceived />}
                        />

                        {/* INVENTORY */}
                        <Route
                            path="inventory"
                            element={<Inventory />}
                        />

                        {/* LOTS */}
                        <Route
                            path="lots"
                            element={<Lots />}
                        />

                        {/* CHAMBERS */}
                        <Route
                            path="chambers"
                            element={<Chambers />}
                        />

                        {/* CUSTOMERS */}
                        <Route
                            path="customers"
                            element={<Customers />}
                        />

                        {/* DELIVERY */}
                        <Route
                            path="delivery"
                            element={<Delivery />}
                        />

                        {/* DELIVERY CHALLANS */}
                        <Route
                            path="delivery-challans"
                            element={<DeliveryChallans />}
                        />

                        {/* CUSTOMER CHARGES */}
                        <Route
                            path="customer-charges"
                            element={<CustomerCharges />}
                        />

                        {/* CASH VOUCHERS - SIDEBAR VISIBLE */}
                        <Route
                            path="cash-vouchers"
                            element={<CashVouchers />}
                        />

                        {/* VEHICLES */}
                        <Route
                            path="vehicles"
                            element={<Vehicles />}
                        />

                        {/* REPORTS */}
                        <Route
                            path="reports"
                            element={<Reports />}
                        />

                        {/* USERS */}
                        <Route
                            path="users"
                            element={<Users />}
                        />

                        {/* SETTINGS */}
                        <Route
                            path="settings"
                            element={<Settings />}
                        />
                    </Route>

                    {/* UNKNOWN ROUTES */}
                    <Route
                        path="*"
                        element={
                            <Navigate
                                to="/dashboard"
                                replace
                            />
                        }
                    />

                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
